import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'

import { scrollIntoViewCompat } from './compat/dom-scroll-polyfills'

type ArrowDir = 'up' | 'down' | 'left' | 'right'

// O Norigin tipa `FocusContext.Provider` de um jeito incompatível com o React
// 18.3 (o do 19 aceita `bigint` no value), então o TS recusa direto. Mesmo cast
// que o ScrollableRow usa. Só entra em cena quando `isFocusBoundary` liga.
const FocusParent = FocusContext.Provider as unknown as React.FC<{
  value: string
  children?: ReactNode
}>

interface FocusableProps {
  children: ReactNode | ((focused: boolean) => ReactNode)
  focusKey?: string | undefined
  onEnterPress?: (() => void) | undefined
  onArrowPress?: ((dir: 'up' | 'down' | 'left' | 'right') => boolean) | undefined
  /** Disparado quando o item recebe foco. Use pra navegação automática (abas, sidebar). */
  onFocus?: (() => void) | undefined
  /** Disparado quando o item perde foco. Use pra pausar autoplay/timers. */
  onBlur?: (() => void) | undefined
  /** Auto-scroll do item pra dentro do viewport quando focado. Default: true. */
  scrollOnFocus?: boolean | undefined
  /**
   * Trap de foco por subárvore (Norigin `isFocusBoundary`): a spatial nav não
   * ESCAPA daqui pros focáveis de fora. É o primitivo que a ficha-overlay usa
   * pra não vazar o D-pad pro catálogo que continua montado atrás (ver
   * docs/plan/tv-ficha-overlay.md e o spike lib/focus-boundary-isolation.test.ts,
   * que prova o vazamento real quando a origem está rolada e o boundary o barra).
   *
   * Quando ligado, este Focusable também PROVÊ o FocusContext pros filhos —
   * eles registram como filhos DESTE nó, que é o que faz o trap valer (a
   * escalada de navegação bate no pai=boundary e para). Sem isso o flag seria
   * inerte: um leaf não tem focáveis-filhos pra confinar. Opt-in — telas que
   * não passam a prop não ganham FocusContext nenhum (comportamento idêntico).
   */
  isFocusBoundary?: boolean | undefined
  /**
   * Restringe o trap a certas direções (default do Norigin quando omitido: TODAS).
   * Ex.: um carrossel usa `['left','right']` (ScrollableRow) pra confinar só o
   * horizontal e deixar ↑/↓ livre. A ficha-overlay omite → confina em todas.
   */
  focusBoundaryDirections?: ArrowDir[] | undefined
  /**
   * Comportamento de click do mouse:
   * - false (default): click foca + dispara onEnterPress (comportamento web típico).
   * - true: 1º click só foca; 2º click no mesmo item dispara onEnterPress.
   * Útil quando onFocus já tem efeito visual (ex: preview ao vivo) e
   * queremos que user "selecione" antes de "ativar". Padrão Apple TV+. */
  clickRequiresFocus?: boolean | undefined
  className?: string | undefined
  style?: CSSProperties | undefined
  disabled?: boolean | undefined
}

export function Focusable(props: FocusableProps) {
  const isBoundary = props.isFocusBoundary === true
  // Spread condicional (não `undefined` explícito) por causa do
  // exactOptionalPropertyTypes — mesmo motivo do fetchPriority.
  const { ref, focused, focusSelf, focusKey } = useFocusable({
    focusKey: props.focusKey ?? '',
    onEnterPress: props.onEnterPress ?? (() => undefined),
    onArrowPress: (dir) =>
      props.onArrowPress
        ? props.onArrowPress(dir as 'up' | 'down' | 'left' | 'right')
        : true,
    focusable: !props.disabled,
    ...(isBoundary ? { isFocusBoundary: true } : {}),
    ...(props.focusBoundaryDirections
      ? { focusBoundaryDirections: props.focusBoundaryDirections }
      : {}),
  })

  // Dispara onFocus/onBlur quando o item ganha/perde foco. Pula primeiro
  // mount via ref pra não disparar onBlur no boot.
  const wasFocusedRef = useRef(false)
  useEffect(() => {
    if (focused) {
      props.onFocus?.()
      wasFocusedRef.current = true
    } else if (wasFocusedRef.current) {
      props.onBlur?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused])

  // Auto-scroll: traz o item pra dentro do viewport (carrossel infinito,
  // grid alto). Comportamento padrão de TVs. Desativável via scrollOnFocus={false}.
  //
  // `scrollIntoViewCompat` e NÃO `el.scrollIntoView({...})` cru: este é o call
  // site mais QUENTE do app (dispara a cada seta do D-pad, em toda tela). No
  // Chr38/53 (o piso real das TVs) o objeto de opções não existe -- o engine
  // coage `{block:'nearest'}` pra `true` e ALINHA AO TOPO. Na prática: cada
  // movimento do foco jogava a lista/rail pro começo em vez de rolar um item.
  // O helper faz o 'nearest' na mão nesses engines (rola o MÍNIMO, e nada se o
  // item já está visível) e usa o nativo no Chr61+. Ver src/compat/.
  useEffect(() => {
    if (!focused) return
    if (props.scrollOnFocus === false) return
    const el = (ref as unknown as React.RefObject<HTMLElement>).current
    // 'nearest' nos 2 eixos: não centraliza, só traz pra dentro da vista.
    scrollIntoViewCompat(el, { behavior: 'smooth', block: 'nearest', inline: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused])

  const rendered =
    typeof props.children === 'function' ? props.children(focused) : props.children
  // Boundary: provê o FocusContext pros filhos registrarem SOB este nó (é o que
  // faz o trap confinar de verdade). Sem boundary, nada muda — os filhos seguem
  // no contexto de foco de cima.
  const content = isBoundary ? <FocusParent value={focusKey}>{rendered}</FocusParent> : rendered

  // Press feedback: scale-down brevemente ao apertar OK ou clicar.
  // Direção oposta ao focus scale (que aumenta) — leitura visual clara
  // de "focado" vs "pressionado". Tier-aware via --press-scale token
  // (1 em vlow/low = sem efeito; 0.96-0.97 em mid+). Reduce-motion zera.
  // Auto-clear via setTimeout pra cobrir casos onde keyup/mouseup
  // não chega (foco perdeu durante press, navegação de rota, etc).
  const [pressed, setPressed] = useState(false)
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerPress = () => {
    if (props.disabled) return
    setPressed(true)
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
    pressTimerRef.current = setTimeout(() => setPressed(false), 120)
  }
  useEffect(() => {
    return () => {
      if (pressTimerRef.current) clearTimeout(pressTimerRef.current)
    }
  }, [])

  // clickRequiresFocus: 1º click só foca (UX Apple TV+ — usuário
  // "seleciona" antes de "ativar"). 2º click no item já focado ativa.
  // Press feedback SÓ dispara em ativação real — 1º click (só foca) não
  // mostra scale-down (UX consistente com Apple TV+, onde nada acontece
  // visualmente ao "selecionar").
  const requiresFocus = props.clickRequiresFocus === true
  const handleClick = () => {
    if (requiresFocus && !focused) {
      focusSelf()
      return
    }
    focusSelf()
    triggerPress()
    props.onEnterPress?.()
  }

  return (
    <div
      ref={ref as unknown as React.RefObject<HTMLDivElement>}
      data-focused={focused || undefined}
      data-pressed={pressed || undefined}
      // aria-disabled: emite quando prop `disabled` true. Permite CSS
      // segmentar visualmente via `[aria-disabled='true']`. Sem isso,
      // CSS de cooldown/disabled ficava morto (M2 review auto-switch
      // 2026-05-11 — PlayerScreen statusOverlayBtn).
      {...(props.disabled ? { 'aria-disabled': true } : {})}
      className={props.className}
      style={props.style}
      onClick={handleClick}
    >
      {content}
    </div>
  )
}
